import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table/index";
import Badge from "../ui/badge/Badge";
import { PayLinkFilterModal } from "./Modals/PayLinkFilterModal";
import { useEffect, useState } from "react";
import Pagination from "../ui/Pagination/Pagination";
import PageMeta from "../common/PageMeta";
import PageBreadcrumb from "../common/PageBreadCrumb";
import PaymentLinkDetailsModal from "./Modals/PaymentLinkDetailsModal";
import { useModal } from "../../hooks/useModal";
import { PayLink } from "./types";
import Spinner from "../ui/Spinner/Spinner";
import { FiLink, FiPlus } from "react-icons/fi";
import { format } from "date-fns";
import { CreatePaymentLinkModal } from "./Modals/CreatePaymentLinkModal";
import { CopyPaymentLinkModal } from "./Modals/CopyPaymentLinkModal";
import { usePaymentLinkStore } from "../../stores/usePaymentLinkStore";
import { RefreshIcon } from "../../icons";
import { getPaymentlinkStatusBadgeColor } from "../../utils/helpers";
import { useLocation } from "react-router";

export default function PaymentLink() {
  const [showFilter, setShowFilter] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedPaymentLink, setSelectedPaymentLink] =
    useState<PayLink | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [linkToCopy, setLinkToCopy] = useState<{
    link: string;
    amount?: string;
  } | null>(null);
  const { paymentLinks, loading, setFilteredPaymentLinks, loadPaymentLinks, filteredPaymentLinks, addPaymentLink, refreshPaymentLinksStatus } = usePaymentLinkStore();
  const location = useLocation();

  const close = () => {
    closeModal();
  };

  // ---- Refresh Payment Link Status
  useEffect(() => {
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const smartRefresh = async () => {
      const isOnPaymentLinkPage = location.pathname === "/payment-link";
      const isTabVisible = document.visibilityState === "visible";

      if (!isOnPaymentLinkPage || !isTabVisible) {
        return;
      }

      try {
        await refreshPaymentLinksStatus();
      } catch (error) {
        console.error("Refresh failed:", error);
      }

      refreshTimeout = setTimeout(smartRefresh, 30000);
    };

    const startRefreshCycle = () => {
      if (location.pathname === "/payment-link") {
        refreshTimeout = setTimeout(smartRefresh, 30000);
      }
    };

    const handleVisibilityChange = () => {
      // Clear existing timeout when tab becomes hidden
      if (document.visibilityState === "hidden") {
        clearTimeout(refreshTimeout);
      }
      // Restart refresh cycle when tab becomes visible again
      else if (
        document.visibilityState === "visible" &&
        location.pathname === "/payment-link"
      ) {
        startRefreshCycle();
      }
    };

    // Start initial refresh cycle
    startRefreshCycle();

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(refreshTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshPaymentLinksStatus, location.pathname]);

  const handleFilterApply = (filters: {
    status: string;
    fromDate: string;
    toDate: string;
  }) => {
    let result = [...paymentLinks];

    if (filters.status && filters.status !== "all") {
      result = result.filter(
        (link) => link.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((link) => new Date(link.createdAt) >= fromDate);
    }

    if (filters.toDate) {
      const toDate = new Date(filters.toDate);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((link) => new Date(link.createdAt) <= toDate);
    }

    setFilteredPaymentLinks(result);
    setCurrentPage(1);
  };

  // ---- For viewing payment link
  const handleViewPaymentLink = (link: PayLink) => {
    addPaymentLink(link);

    setLinkToCopy({
      link: `${import.meta.env.VITE_PAYLINK_BASE_URL}/payment-link/pay?token=${
        link.token
      }`,
      amount: `${link.amount.toLocaleString()} ${
        link.paymentDetails?.currency === "CNGN" ? "cNGN" : link.paymentDetails?.currency
      }`,
    });
    setShowCopyModal(true);
  };

  const handleOpenDetailsModal = (link: PayLink) => {
    setSelectedPaymentLink(link);
    openModal();
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const totalPages = Math.ceil(filteredPaymentLinks.length / itemsPerPage);
  const paginatedData = filteredPaymentLinks.slice(
    (currentPage - 1) * itemsPerPage,
    Math.min(currentPage * itemsPerPage, filteredPaymentLinks.length)
  );


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Payment Links"
        description="Create and manage your payment links"
      />
      <PageBreadcrumb pageTitle="Payment Links" />

      <div className="mb-6 overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Create Payment Link
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Generate a link to receive payments from customers
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-theme-sm font-medium text-white shadow-theme-xs hover:bg-blue-700"
          >
            <FiPlus className="w-4 h-4" />
            Create Payment Link
          </button>
        </div>
      </div>

      <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Payment Link History
          </h3>
          <div className="flex items-center gap-1">
            <button onClick={loadPaymentLinks} className="refresh-button">
              <RefreshIcon className="w-6 h-6" />
            </button>
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Filter
            </button>
            <PayLinkFilterModal
              isOpen={showFilter}
              onClose={() => setShowFilter(false)}
              onApply={handleFilterApply}
            />
          </div>
        </div>
        

        {filteredPaymentLinks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500 border-b border-gray-100 dark:border-gray-800">
            <FiLink className="w-12 h-12 mb-4" />
            <p className="text-lg mb-2">No payment links found</p>
            <p className="text-sm mb-4">Try adjusting your filters</p>
            <button
              onClick={() => setShowFilter(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
            >
              Adjust Filters
            </button>
          </div>
        )}

        <div className="max-w-full overflow-x-auto">
          <div className="hidden sm:block">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Link
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Date
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((link) => (
                  <TableRow key={link._id}>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {link.amount.toLocaleString()} {" "}
                      {link.currency?.abbr === "CNGN" ? "cNGN" : link.currency?.abbr}
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleViewPaymentLink(link)} // This opens copy modal
                        className="text-blue-600 hover:underline text-theme-sm dark:text-blue-400"
                        title="Copy payment link"
                      >
                        View Link
                      </button>
                    </TableCell>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {formatDate(link.createdAt)}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge size="sm" color={getPaymentlinkStatusBadgeColor(link.status)}>
                        {link.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleOpenDetailsModal(link)} // This opens details modal
                        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="View payment link details"
                      >
                        View
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="sm:hidden">
            <Table>
              <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
                <TableRow>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Link
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHeader>

              <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paginatedData.map((link) => (
                  <TableRow key={link._id}>
                    <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                      {link.amount.toLocaleString()} {" "}
                      {link.currency?.abbr === "CNGN" ? "cNGN" : link.currency?.symbol}
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleViewPaymentLink(link)} // This opens copy modal
                        className="text-blue-600 hover:underline text-theme-sm dark:text-blue-400"
                        title="Copy payment link"
                      >
                        View Link
                      </button>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge size="sm" color={getPaymentlinkStatusBadgeColor(link.status)}>
                        {link.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3">
                      <button
                        onClick={() => handleOpenDetailsModal(link)} // Details modal
                        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        View
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {filteredPaymentLinks.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <PaymentLinkDetailsModal
        isOpen={isOpen}
        onClose={close}
        payLink={selectedPaymentLink}
      />

      <CreatePaymentLinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={(createdLink) => {
          setTimeout(() => {
          setLinkToCopy({
            link: `${
              import.meta.env.VITE_PAYLINK_BASE_URL
            }/payment-link/pay?token=${createdLink.token}`,
            amount: `${createdLink.amount.toLocaleString()} ${createdLink.paymentDetails?.currency === 'CNGN' ? 'cNGN' : createdLink.paymentDetails?.currency || ''} `});
          setShowCopyModal(true);
          addPaymentLink(createdLink);
          }, 0);
        }}
      />

      <CopyPaymentLinkModal
        isOpen={showCopyModal}
        onClose={() => {
          setShowCopyModal(false);
          setLinkToCopy(null);
          setShowCreateModal(false);
        }}
        paymentLink={linkToCopy?.link || ""}
        amount={linkToCopy?.amount}
      />
    </>
  );
}
